import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';

/**
 * Thin wrapper over amazon-cognito-identity-js (SRP flow, public client). The
 * pool/client IDs are baked at build time via Vite env; when absent (local dev)
 * `pool()` returns null and the whole auth layer is bypassed — the LoginGate
 * renders the app directly and the WS uses the static dev token.
 *
 * IDs are NOT secrets: they ship in the SPA bundle. Security comes from the
 * proxy verifying the access-token JWT against the pool JWKS on the WS upgrade.
 */
function env(): { VITE_COGNITO_USER_POOL_ID?: string; VITE_COGNITO_CLIENT_ID?: string } {
  return (import.meta as unknown as { env?: Record<string, string> }).env ?? {};
}

let cached: CognitoUserPool | null | undefined;

export function pool(): CognitoUserPool | null {
  if (cached !== undefined) return cached;
  const { VITE_COGNITO_USER_POOL_ID: UserPoolId, VITE_COGNITO_CLIENT_ID: ClientId } = env();
  cached = UserPoolId && ClientId ? new CognitoUserPool({ UserPoolId, ClientId }) : null;
  return cached;
}

/** True when Cognito is configured (prod build). */
export function authEnabled(): boolean {
  return pool() !== null;
}

export interface SignInOk { kind: 'ok'; session: CognitoUserSession; email: string; }
export interface NewPasswordRequired { kind: 'newPassword'; user: CognitoUser; email: string; }
export type SignInResult = SignInOk | NewPasswordRequired;

/**
 * SRP sign-in. Resolves to `newPassword` when Cognito issues the
 * NEW_PASSWORD_REQUIRED challenge (first login after admin-create-user) — the
 * caller then collects a new password and calls `completeNewPassword`.
 */
export function signIn(email: string, password: string): Promise<SignInResult> {
  const p = pool();
  if (!p) return Promise.reject(new Error('auth not configured'));
  const user = new CognitoUser({ Username: email, Pool: p });
  const details = new AuthenticationDetails({ Username: email, Password: password });
  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: (session) => resolve({ kind: 'ok', session, email }),
      onFailure: (err) => reject(err),
      newPasswordRequired: () => resolve({ kind: 'newPassword', user, email }),
    });
  });
}

/** Resolve a NEW_PASSWORD_REQUIRED challenge with the user's chosen password. */
export function completeNewPassword(user: CognitoUser, newPassword: string): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: (session) => resolve(session),
      onFailure: (err) => reject(err),
    });
  });
}

/** Restore a persisted session (validity-checked, auto-refreshes). null if none. */
export function currentSession(): Promise<{ session: CognitoUserSession; email: string } | null> {
  const p = pool();
  if (!p) return Promise.resolve(null);
  const user = p.getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) { resolve(null); return; }
      resolve({ session, email: user.getUsername() });
    });
  });
}

/**
 * Start the forgot-password flow: Cognito emails a verification code to the
 * address (if it exists — `preventUserExistenceErrors` keeps this from leaking
 * which emails are registered). Pair with `confirmForgotPassword`.
 */
export function forgotPassword(email: string): Promise<void> {
  const p = pool();
  if (!p) return Promise.reject(new Error('auth not configured'));
  const user = new CognitoUser({ Username: email, Pool: p });
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
      // inputVerificationCode also fires on success; resolving once is enough.
      inputVerificationCode: () => resolve(),
    });
  });
}

/** Complete forgot-password with the emailed code + the user's new password. */
export function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  const p = pool();
  if (!p) return Promise.reject(new Error('auth not configured'));
  const user = new CognitoUser({ Username: email, Pool: p });
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export function signOut(): void {
  pool()?.getCurrentUser()?.signOut();
}

export function accessToken(session: CognitoUserSession): string {
  return session.getAccessToken().getJwtToken();
}
